// src/components/subscription/UpgradeSubscriptionForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.js';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { Badge } from '../ui/badge.js';
import { CreditCard, Lock, CheckCircle, AlertTriangle, Star, Crown, Zap, Users, Calendar, Heart, Target, TrendingUp, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext.js';
import { useSubscription } from '../../contexts/SubscriptionContext.js';
import { getError } from '../../utils/errorMessages.js';
import { loadStripe } from '@stripe/stripe-js';

// Load Stripe with publishable key from environment
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripeKey) {
  console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set');
}
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const UpgradeSubscriptionForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription, upgradeSubscription, formatTierName } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get current tier or default to basic
  const currentTier = subscription?.tier || 'basic';
  
  // Define available upgrade tiers with enhanced styling
  const upgradeTiers = [
    { 
      id: 'premium', 
      name: 'Premium', 
      price: '$19',
      period: '/month',
      icon: Star,
      color: 'bg-gradient-to-br from-indigo-500 to-purple-600',
      borderColor: 'border-indigo-200',
      badgeColor: 'bg-indigo-100 text-indigo-800',
      features: [
        'Unlimited workout tracking & analytics',
        'Personalized exercise recommendations',
        'Access to live weekly guidance sessions',
        'Advanced safety features & modifications',
        'Smart calendar & scheduling system',
        'Priority email & chat support',
        'Progress visualization & insights',
        'Custom workout plan builder'
      ],
      highlight: 'Most Popular'
    },
    { 
      id: 'elite', 
      name: 'Elite', 
      price: '$39',
      period: '/month',
      icon: Crown,
      color: 'bg-gradient-to-br from-purple-600 to-pink-600',
      borderColor: 'border-purple-200',
      badgeColor: 'bg-purple-100 text-purple-800',
      features: [
        'Full guided workout videos with professional trainers',
        '⭐ Family Challenge Setting - Monitor and track family members\' progress together',
        'AI-powered training and form analysis',
        'Emergency contact integration',
        'Priority customer support'
      ],
      highlight: 'Best Value'
    }
  ].filter(tier => {
    // Filter tiers based on current subscription
    const tierRanking = { basic: 0, premium: 1, elite: 2 };
    return tierRanking[tier.id] > tierRanking[currentTier];
  });
  
  // If no upgrades available (already on highest tier)
  if (upgradeTiers.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-8 text-center">
          <div className="mb-6">
            <Crown className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">You're Already on Our Best Plan!</h2>
            <p className="text-gray-600">You're currently subscribed to our Elite tier, which includes all features.</p>
          </div>
          <Button 
            onClick={() => navigate('/dashboard')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium"
          >
            Return to Dashboard
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  const handleUpgrade = async (tier) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('=== UPGRADE DEBUG START ===');
      console.log('Upgrading to tier:', tier);
      console.log('Current subscription:', subscription);
      console.log('Stripe key being used (first 8 chars):', stripeKey.substring(0, 8));
      
      // Call API to upgrade or start Checkout for the chosen tier (premium or elite)
      const result = await upgradeSubscription(tier);
      
      console.log('Upgrade subscription result:', result);
      
      if (!result.success) {
        setError(result.error || 'Unable to start checkout. Please try again.');
        return;
      }
      
      // First paid plan (free/basic → premium or elite): Stripe Checkout
      if (result.url || result.sessionId) {
        if (result.url) {
          window.location.href = result.url;
          return;
        }

        if (!stripePromise) {
          throw new Error('Stripe is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY.');
        }
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Failed to load Stripe.');
        }
        console.log('About to redirect to checkout with session ID:', result.sessionId);
        
        const { error: checkoutError } = await stripe.redirectToCheckout({
          sessionId: result.sessionId
        });
        
        if (checkoutError) {
          console.error('Error redirecting to checkout:', checkoutError);
          setError(checkoutError.message || 'Could not open Stripe Checkout. Please try again.');
        }
        console.log('=== UPGRADE DEBUG END ===');
        return;
      }
      
      // Paid-to-paid upgrade (e.g. premium → elite with an existing Stripe sub)
      alert(result.message || `Successfully upgraded to ${tier}!`);
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/subscription/manage');
      }
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      setError(getError(err, 'Unable to start checkout. Please try again or contact support.'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header Section */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Upgrade Your Experience</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          You're currently on the <span className="font-semibold text-indigo-600">{formatTierName(currentTier)}</span> plan. 
          Choose your upgrade to unlock powerful features designed for your fitness journey.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          {error}
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {upgradeTiers.map(tier => {
          const IconComponent = tier.icon;
          
          return (
            <Card 
              key={tier.id} 
              id={`${tier.id}-plan`}
              className="relative transition-all duration-200 hover:shadow-lg"
            >
              {/* Highlight Badge */}
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className={`${tier.badgeColor} px-3 py-1 text-xs font-medium`}>
                    {tier.highlight}
                  </Badge>
                </div>
              )}
              
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${tier.color}`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-gray-900">{tier.name}</CardTitle>
                    <p className="text-sm text-gray-500">Perfect for active seniors</p>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                {/* Pricing */}
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">{tier.price}</span>
                    <span className="text-gray-500 ml-1">{tier.period}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Cancel anytime • No setup fees</p>
                </div>
                
                {/* Features List */}
                <div className="space-y-3 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">What's included:</h4>
                  {tier.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                
                {/* Direct Upgrade Button */}
                <Button
                  onClick={() => handleUpgrade(tier.id)}
                  disabled={loading}
                  className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
                    tier.id === 'premium' 
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl'
                      : 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span>Upgrade to {tier.name}</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Security & Trust */}
      <div className="mt-8 text-center">
        <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <Lock className="w-4 h-4" />
            <span>Secure Payment</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4" />
            <span>Cancel Anytime</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeSubscriptionForm;